import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import { NgSelectComponent } from "@ng-select/ng-select";
import { NavController, NavParams, ViewController } from "ionic-angular";
import SortOptions from "models/content-wrapper/sort-options";
import {
  DropdownItem,
  Order,
  OrderComponent,
} from "models/order-management/models";
import { isProfessionalProvider } from "models/resource-management/company-resource-type-guards";
import CompanyResourceType from "models/resource-management/company-resource-type.enum";
import { OrderFormState } from "pages/orders-entry-om/add-order/order-form-state";
import { ResourceManagementService } from "providers/resource-management/resource-management.service";
import { Observable, Subject } from "rxjs";
import { of } from "rxjs/observable/of";
import {
  catchError,
  distinctUntilChanged,
  filter,
  mergeMap,
  switchMap,
} from "rxjs/operators";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import {
  OrderComponentInterface,
  OrderComponentPropertyUpdate,
  OrderCreateRequestUpdate,
} from "../order-components-interface";

@Component({
  selector: "ordering-provider",
  templateUrl: "ordering-provider.html",
})
export class OrderingProviderComponent implements OrderComponentInterface {
  /****************************************************************************
   * Inputs                                                                   *
   ****************************************************************************/
  @Input() orderComponent: OrderComponent;
  @Input() orderCreateRequest: Order;
  @Input() get orderFormState(): OrderFormState {
    return this._orderFormState;
  }

  /****************************************************************************
   * Outputs                                                                  *
   ****************************************************************************/
  @Output() orderCreateRequestEvent =
    new EventEmitter<OrderCreateRequestUpdate>();
  @Output() orderFormStateEvent = new EventEmitter<OrderFormState>();
  @Output() isValidEvent = new EventEmitter<boolean>();
  @Output() amendedOrderRequestEvent =
    new EventEmitter<OrderComponentPropertyUpdate>();

  /****************************************************************************
   * ViewChild                                                                *
   ****************************************************************************/
  @ViewChild("orderingProviderId") orderingProviderInput: NgSelectComponent;
  @ViewChild("hiddenInput") hiddenInput: {
    setFocus: () => void;
  };

  /****************************************************************************
   * State                                                                    *
   ****************************************************************************/
  providerDropdownItems$: Observable<DropdownItem[]>;
  providerQuery$ = new Subject<string>();
  selectedProvider: string = "";
  currentProviderId = 0;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public viewCtrl: ViewController,
    public stateData: StateManagementService,
    private resourceManagementService: ResourceManagementService
  ) {}
  private _orderFormState: OrderFormState;

  /****************************************************************************
   * Lifecycle Methods                                                        *
   ****************************************************************************/
  ngOnInit() {
    this.currentProviderId =
      this.orderFormState.credentialedResource.getProviderId();
    this.providerSearch();
    this.checkIsValid();
  }

  /****************************************************************************
   * Getter Methods                                                           *
   ****************************************************************************/
  public get shouldShow() {
    return !this._orderFormState.declined;
  }

  /****************************************************************************
   * Setter Methods                                                           *
   ****************************************************************************/
  set orderFormState(value: OrderFormState) {
    this._orderFormState = value;
    if (this._orderFormState.declined && !!this.selectedProvider) {
      this.selectedProvider = undefined;
      const componentField = this.orderComponent.properties[0].name;
      this.orderCreateRequestEvent.emit({
        deletes: [componentField as keyof Order, "orderingProviderId"],
      });
    }
    this.checkIsValid();
  }

  /****************************************************************************
   * Component Related Methods                                                *
   ****************************************************************************/

  /**
   * Sets the the providerDropdownItems observable only if the user is not a provider.
   * Otherwise, the provider is the ordering provider and the field is disabled.
   * @returns
   */
  providerSearch() {
    if (isProfessionalProvider(this.orderFormState.credentialedResource)) {
      const provider = this.orderFormState.credentialedResource;
      const event: DropdownItem = {
        label: provider.findDisplayName(new Date()),
        value: `${provider.externalId}`,
      };
      this.onProviderSelected(event);
    }
    this.providerDropdownItems$ = this.providerQuery$.pipe(
      distinctUntilChanged(),
      filter((query) => !query || ![1, 2].includes(query.length)), // do nothing if 1 or 2 characters
      switchMap((query: string | null) => {
        // null -> clear, '' -> field cleared by backspace
        if (!query || query.length === 0) {
          return of([]);
        }
        return this.resourceManagementService
          .getCompanyResourceSummaries(
            0,
            100,
            query ? query : undefined,
            new SortOptions("last_name", "asc"),
            CompanyResourceType.PROVIDER
          )
          .pipe(
            mergeMap((providerList) =>
              of(
                providerList.content.map((provider) => {
                  return {
                    label: provider.displayName,
                    value: `${provider.externalId}`,
                  };
                })
              )
            ),
            catchError(() => of([]))
          );
      })
    );
  }

  checkIsValid() {
    if (
      this.orderComponent.properties[0].required &&
      (!this.selectedProvider || this.selectedProvider.length === 0)
    ) {
      if (this._orderFormState.declined) {
        this.isValidEvent.emit(true);
      } else {
        this.isValidEvent.emit(false);
      }
    } else {
      this.isValidEvent.emit(true);
    }
  }

  onProviderSelected(event: DropdownItem) {
    if (event) {
      this.orderFormStateEvent.emit({
        isSameProvider: this.isSameProvider(event.value),
        approvalType: undefined,
      });
      this.selectedProvider = event.label;
      this.orderCreateRequestEvent.emit({
        updates: {
          orderingProviderId: parseInt(event.value),
          orderingProviderDisplayName: event.label,
        },
      });
    }
    this.checkIsValid();
  }

  isSameProvider(selectedProviderId) {
    return parseInt(selectedProviderId) === this.currentProviderId;
  }

  // Hackish fix to show keyboard on IOS / Safari
  onClick() {
    this.hiddenInput.setFocus();
    this.orderingProviderInput.filterInput.nativeElement.focus();
  }

  disableField() {
    // Rewrote this to be a little more human-readable
    if (
      !this.orderComponent.properties[0].amendable &&
      this.orderFormState.formType === "amendment"
    ) {
      return true;
    }
    return false;
  }

  /****************************************************************************
   * Cypress-Related ("data-cy") Methods                                      *
   ****************************************************************************/
  toKebabCase(label: string): string {
    if (label) {
      return label
        .replace(/^\s+|\s+$/gm, "")
        .replace(/([a-z])([A-Z])/g, "$1-$2")
        .replace(/[\s_]+/g, "-")
        .toLowerCase();
    }
    return "";
  }

  createDataCyAttribute(component: string, option: string): string {
    return `${this.toKebabCase(component)}-option-${this.toKebabCase(option)}`;
  }
}



// WEBPACK FOOTER //
// ./src/components/order-management/ordering-provider/ordering-provider.ts